import { classnames } from '@bem-react/classnames'
import { animateTimeout, cnMixPopoverAnimate } from '@consta/uikit/MixPopoverAnimate'
import type { Props as PopoverProps } from '@consta/uikit/Popover'
import { useModalManager } from '@library/model'
import type { ComponentProps, ElementRef, FC, PropsWithChildren, RefObject } from 'react'
import { useRef } from 'react'
import { Transition } from 'react-transition-group'

import { PlaceablePopover } from '../PlaceablePopover'

type AnimatedPopoverProps = ComponentProps<'div'> & {
  className?: string
  anchorRef: RefObject<HTMLElement>
  isOpen: boolean
} & Pick<PopoverProps, 'direction' | 'offset' | 'isInteractive' | 'onClickOutside'>

/**
 * Нестилизованный механизм открытия всплывающих окон
 *
 * Использует react-transition-group
 * Основные пропсы берутся из `@consta: Popover`
 *
 * @param props.anchorRef - ссылка элемент на элемент, относительно которого открывается окно
 * @param props.isOpen - открыто ли сейчас окно
 */
export const AnimatedPopover: FC<PropsWithChildren<AnimatedPopoverProps>> = ({
  children,
  className,
  anchorRef,
  isOpen,
  ...popoverProps
}) => {
  const { other } = useModalManager()
  const popoverRef = useRef<ElementRef<'div'>>(null)

  return (
    <Transition
      in={isOpen}
      timeout={animateTimeout}
      nodeRed={popoverRef}
      mountOnEnter={true}
      unmountOnExit={true}>
      {animate => (
        <PlaceablePopover
          ref={popoverRef}
          className={classnames(cnMixPopoverAnimate({ animate }), className)}
          anchorRef={anchorRef}
          container={other.current ?? undefined}
          {...popoverProps}>
          {children}
        </PlaceablePopover>
      )}
    </Transition>
  )
}
